import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { Gift, Loader2 } from "lucide-react";
import api from "../api";

/**
 * Big-screen live check-in wall. Polls stats every few seconds and shows a
 * running count + a ticker of recent arrivals. If the organizer enabled
 * gamified entry, a "Draw winner" button runs a lucky draw among checked-in guests.
 * Route: /events/:eventId/wall
 */
export default function CheckInWallPage() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [stats, setStats] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [winner, setWinner] = useState(null);
  const [drawing, setDrawing] = useState(false);
  const drawTimer = useRef(null);

  useEffect(() => {
    api.get(`/events/${eventId}/`).then((r) => setEvent(r.data)).catch(() => {});
  }, [eventId]);

  const poll = useCallback(async () => {
    try {
      const r = await api.get(`/checkin/events/${eventId}/stats/`);
      setStats(r.data);
    } catch (e) {
      if (e.response?.status === 403) setForbidden(true);
    }
  }, [eventId]);

  useEffect(() => {
    poll();
    const id = setInterval(poll, 4000);
    return () => clearInterval(id);
  }, [poll]);

  const runDraw = async () => {
    setDrawing(true);
    setWinner(null);
    // Suspense: cycle names briefly, then reveal
    const recent = stats?.recent_check_ins || [];
    let i = 0;
    drawTimer.current = setInterval(() => {
      if (recent.length) setWinner({ teaser: recent[i % recent.length]?.attendee_name });
      i += 1;
    }, 90);
    try {
      const r = await api.post(`/checkin/events/${eventId}/raffle/`);
      setTimeout(() => {
        clearInterval(drawTimer.current);
        setWinner(r.data.winner ? { name: r.data.winner.attendee_name, seat: r.data.winner.seat_number } : null);
        setDrawing(false);
      }, 1800);
    } catch (e) {
      clearInterval(drawTimer.current);
      setDrawing(false);
    }
  };

  if (forbidden) {
    return <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center p-6 text-center">
      Only the event organizer can open the live wall.</div>;
  }
  if (!stats) {
    return <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  const recent = stats.recent_check_ins || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-indigo-950 to-gray-950 text-white flex flex-col">
      <div className="px-8 pt-8 text-center">
        <p className="text-indigo-300 uppercase tracking-[0.3em] text-sm">Now checking in</p>
        <h1 className="text-3xl md:text-5xl font-bold mt-2">{stats.event_title}</h1>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="text-center">
          <div className="text-[18vw] md:text-[12rem] font-black leading-none bg-gradient-to-r from-indigo-400 to-pink-400 bg-clip-text text-transparent">
            {stats.checked_in}
          </div>
          <p className="text-2xl md:text-3xl text-gray-300 mt-2">
            of {stats.total_tickets} guests in · <span className="text-green-400">{stats.percent_in}%</span>
          </p>
        </div>

        {/* Recent arrivals ticker */}
        <div className="mt-10 w-full max-w-3xl px-6">
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-3 text-center">Latest arrivals</p>
          <div className="flex flex-wrap justify-center gap-3">
            {recent.slice(0, 10).map((t) => (
              <span key={t.id} className="px-4 py-2 rounded-full bg-white/10 backdrop-blur text-sm">
                ✅ {t.attendee_name}{t.tier_name ? ` · ${t.tier_name}` : ""}
              </span>
            ))}
            {recent.length === 0 && <span className="text-gray-500">Waiting for the first guest…</span>}
          </div>
        </div>
      </div>

      {/* Gamified entry */}
      {event?.enable_gamified_entry && (
        <div className="pb-10 text-center">
          {winner && (winner.name || winner.teaser) && (
            <div className={`mb-5 ${winner.name ? "animate-bounce" : "opacity-70"}`}>
              <p className="text-sm uppercase tracking-widest text-pink-300">{winner.name ? "🎉 Winner 🎉" : "Drawing…"}</p>
              <p className="text-4xl md:text-6xl font-black mt-1">{winner.name || winner.teaser}</p>
              {winner.seat && <p className="text-gray-300 mt-1">Seat {winner.seat}</p>}
            </div>
          )}
          <button
            onClick={runDraw}
            disabled={drawing}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-pink-500 to-indigo-500 text-white text-lg font-bold shadow-lg hover:scale-105 transition disabled:opacity-60"
          >
            <Gift /> {drawing ? "Drawing…" : "Draw a winner"}
          </button>
        </div>
      )}
    </div>
  );
}
